import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Flex, Text, Card, Badge, Button, Tooltip, Dialog, TextField,
  IconButton, AlertDialog, Table, Box, Popover, CheckboxGroup, Checkbox,
} from "@radix-ui/themes";
import {
  Pencil1Icon, TrashIcon, PersonIcon, BookmarkIcon, ArrowLeftIcon,
  MagnifyingGlassIcon, MixerHorizontalIcon, ReloadIcon, ChevronLeftIcon,
  ChevronRightIcon, CheckCircledIcon, CrossCircledIcon,
} from "@radix-ui/react-icons";
import {
  useContactTags, useCreateContactTag, useUpdateContactTag, useDeleteContactTag,
  useContacts,
} from "hooks";
import { formatInternationalPhoneNumber } from "helpers/phoneFormatting";

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ── Contact table configuration (mirrors the Contacts / Segments tabs) ───────
const SOURCE_LABELS = {
  guest_portal: "Guest Portal", sevenrooms: "SevenRooms", opentable: "OpenTable",
  eat_app: "Eat App", mailchimp: "Mailchimp", wix: "Wix", manual: "Manual",
  Opentable: "OpenTable", Mailchimp: "Mailchimp", Wix: "Wix",
};
const SOURCE_COLORS = {
  guest_portal: "blue", sevenrooms: "green", opentable: "orange",
  eat_app: "jade", mailchimp: "yellow", wix: "violet", manual: "purple",
  Opentable: "orange", Mailchimp: "yellow", Wix: "violet",
};
const GENDER_LABELS = {
  male: "Male", female: "Female", other: "Other", unknown: "Unknown", no_response: "No response",
};
const AGE_GROUP_LABELS = {
  under_18: "Under 18", "18_24": "18-24", "25_34": "25-34", "35_44": "35-44",
  "45_54": "45-54", "55_64": "55-64", "65_plus": "65+", no_response: "No response",
};

const CONTACT_TABLE_COLUMNS = [
  { key: "name", label: "Name", width: "12%" },
  { key: "email", label: "Email", width: "16%" },
  { key: "phone", label: "Phone", width: "10%" },
  { key: "source", label: "Source", width: "8%" },
  { key: "email_marketing", label: "Email Consent", width: "9%" },
  { key: "sms_marketing", label: "SMS Consent", width: "9%" },
  { key: "whatsapp_marketing", label: "WhatsApp Consent", width: "10%" },
  { key: "gender", label: "Gender", width: "7%" },
  { key: "age_group", label: "Age Group", width: "8%" },
  { key: "locations", label: "Locations", width: "11%" },
  { key: "total_visits", label: "Visits", width: "7%" },
  { key: "first_visit", label: "First Visit", width: "10%" },
  { key: "last_visit", label: "Last Visit", width: "10%" },
  { key: "created_at", label: "Created", width: "10%" },
  { key: "updated_at", label: "Updated", width: "10%" },
];

const DEFAULT_VISIBLE_COLUMNS = [
  "name", "email", "phone", "source", "email_marketing", "sms_marketing", "whatsapp_marketing", "locations",
];

// ── Truncated text identical to the contacts tab ────────────────────────────

const TruncatedText = ({ children, size, weight, color }) => {
  const text = typeof children === "string" ? children : String(children ?? "");
  const [isTruncated, setIsTruncated] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const check = () => {
      const el = containerRef.current;
      if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [text]);

  const textEl = (
    <Box as="div" ref={containerRef}
      style={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      <Text size={size} weight={weight} color={color}>{text}</Text>
    </Box>
  );
  return isTruncated ? <Tooltip content={text}>{textEl}</Tooltip> : textEl;
};

const ConsentBadge = ({ value }) => (
  <Badge color={value ? "green" : "gray"} variant="soft" size="1">
    {value ? <CheckCircledIcon width="12" height="12" /> : <CrossCircledIcon width="12" height="12" />}
    <Text size="1">{value ? "Opted-in" : "No"}</Text>
  </Badge>
);

// ══════════════════════════════════════════════════════════════════════════════
//  Tag Contacts Viewer (drills into a tag)
// ══════════════════════════════════════════════════════════════════════════════

function TagContactsViewer({ tag, orgId, onBack, setToastMessage }) {
  const [filters, setFilters] = useState({ search: "", page: 1, page_size: 25 });
  const [searchInput, setSearchInput] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);

  useEffect(() => {
    const t = setTimeout(() => setFilters((p) => ({ ...p, search: searchInput, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { contacts, pagination, isLoading, refetch } = useContacts(orgId, {
    tag_id: tag?.tag_id,
    search: filters.search,
    page: filters.page,
    page_size: filters.page_size,
  });

  const visibleColumnSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  const isColumnVisible = (columnKey) => visibleColumnSet.has(columnKey);

  const toggleAllColumns = () => {
    setVisibleColumns((prev) => (
      prev.length === CONTACT_TABLE_COLUMNS.length ? [] : CONTACT_TABLE_COLUMNS.map((column) => column.key)
    ));
  };

  const renderContactCell = (contact, columnKey) => {
    switch (columnKey) {
      case "name":
        return <TruncatedText weight="regular" size="2">{contact.full_name || "—"}</TruncatedText>;
      case "email":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {contact.email_address?.endsWith("@noemailbwifi.com") ? "—" : contact.email_address}
          </TruncatedText>
        );
      case "phone":
        return <TruncatedText weight="regular" size="2" color="gray">{formatInternationalPhoneNumber(contact.phone_number) || "—"}</TruncatedText>;
      case "source":
        return (
          <Badge color={SOURCE_COLORS[contact.source] || "gray"} variant="soft" size="1">
            {SOURCE_LABELS[contact.source] || contact.source || "—"}
          </Badge>
        );
      case "email_marketing":
        return <ConsentBadge value={contact.email_marketing} />;
      case "sms_marketing":
        return <ConsentBadge value={contact.sms_marketing} />;
      case "whatsapp_marketing":
        return <ConsentBadge value={contact.whatsapp_marketing} />;
      case "gender":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {GENDER_LABELS[contact.gender] || contact.gender || "—"}
          </TruncatedText>
        );
      case "age_group":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {AGE_GROUP_LABELS[contact.age_group] || contact.age_group || "—"}
          </TruncatedText>
        );
      case "locations":
        return (
          <TruncatedText weight="regular" size="2" color="gray">
            {contact.location_names?.length ? contact.location_names.join(", ") : "—"}
          </TruncatedText>
        );
      case "total_visits":
        return <TruncatedText weight="regular" size="2" color="gray">{contact.total_visits ?? 0}</TruncatedText>;
      case "first_visit":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.first_visit)}</TruncatedText>;
      case "last_visit":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.last_visit)}</TruncatedText>;
      case "created_at":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.created_at)}</TruncatedText>;
      case "updated_at":
        return <TruncatedText weight="regular" size="2" color="gray">{formatDateTime(contact.updated_at)}</TruncatedText>;
      default:
        return null;
    }
  };

  return (
    <Flex direction="column" gap="4" style={{ alignSelf: "stretch", flex: "1 0 0", width: "100%" }}>
      {/* Back button + header */}
      <Flex align="center" gap="3" style={{ alignSelf: "stretch" }}>
        <IconButton variant="soft" color="gray" onClick={onBack}>
          <ArrowLeftIcon />
        </IconButton>
        <Flex align="center" gap="2">
          <Badge variant="outline" size="3" color="teal">
            <BookmarkIcon width="12" height="12" />
            {tag.name}
          </Badge>
        </Flex>
        <Badge variant="soft" size="2" color="gray" style={{ marginLeft: "auto" }}>
          {pagination.count} contact{pagination.count !== 1 && "s"}
        </Badge>
      </Flex>

      {/* Search + table controls */}
      <Flex gap="3" align="center" wrap="wrap" justify="between" style={{ alignSelf: "stretch" }}>
        <Box style={{ width: 300 }}>
          <TextField.Root placeholder="Search within tag…" value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}>
            <TextField.Slot><MagnifyingGlassIcon height="16" width="16" /></TextField.Slot>
          </TextField.Root>
        </Box>

        <Flex gap="2" align="center">
          <Popover.Root>
            <Popover.Trigger asChild>
              <Button variant="soft" color="gray">
                <MixerHorizontalIcon />
                Columns
              </Button>
            </Popover.Trigger>
            <Popover.Content maxHeight="320px" style={{ width: "220px", padding: 0 }}>
              <Flex direction="column" gap="1" p="2">
                <Flex p="2" align="center" gap="2">
                  <Checkbox
                    size="2"
                    checked={visibleColumns.length === CONTACT_TABLE_COLUMNS.length}
                    onCheckedChange={toggleAllColumns}
                  />
                  <Text size="2">Select all</Text>
                </Flex>
                <CheckboxGroup.Root
                  size="2"
                  value={visibleColumns}
                  onValueChange={setVisibleColumns}
                  style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}
                >
                  {CONTACT_TABLE_COLUMNS.map((column) => (
                    <Flex key={column.key} p="2">
                      <CheckboxGroup.Item value={column.key}>{column.label}</CheckboxGroup.Item>
                    </Flex>
                  ))}
                </CheckboxGroup.Root>
              </Flex>
            </Popover.Content>
          </Popover.Root>
          <Button variant="soft" color="gray" onClick={() => { refetch(); setToastMessage?.("Contacts refreshed"); }}>
            <ReloadIcon />
            Refresh
          </Button>
        </Flex>
      </Flex>

      {/* Table */}
      <Card style={{ display: "flex", alignSelf: "stretch", flexDirection: "column", alignItems: "flex-start",
          flex: "1 0 0", height: "100%", maxWidth: "100%", overflow: "auto" }}>
        {contacts.length === 0 ? (
          <Flex justify="center" align="center" style={{ width: "100%", height: "100%" }}>
            <Text color="gray">{isLoading ? "Loading contacts…" : "No contacts with this tag"}</Text>
          </Flex>
        ) : (
          <Table.Root layout="fixed" style={{ width: "100%", height: "100%" }}>
            <Table.Header style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "var(--color-panel-solid)" }}>
              <Table.Row align="center">
                {CONTACT_TABLE_COLUMNS.filter((column) => isColumnVisible(column.key)).map((column) => (
                  <Table.ColumnHeaderCell key={column.key} style={{ width: column.width }}>
                    <TruncatedText size="2" weight="medium">{column.label}</TruncatedText>
                  </Table.ColumnHeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {contacts.map((c) => (
                <Table.Row key={c.id}>
                  {CONTACT_TABLE_COLUMNS.filter((column) => isColumnVisible(column.key)).map((column) => (
                    <Table.Cell key={column.key}>{renderContactCell(c, column.key)}</Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Card>

      {/* Pagination */}
      {pagination.numPages > 1 && (
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <Text size="2" color="gray">
            Showing {(pagination.currentPage - 1) * filters.page_size + 1} –{" "}
            {Math.min(pagination.currentPage * filters.page_size, pagination.count)} of{" "}
            {pagination.count} contacts
          </Text>
          <Flex gap="2" align="center">
            <Button variant="soft" disabled={pagination.currentPage <= 1}
              onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}>
              <ChevronLeftIcon /> Previous
            </Button>
            <Text size="2">Page {pagination.currentPage} of {pagination.numPages}</Text>
            <Button variant="soft" disabled={pagination.currentPage >= pagination.numPages}
              onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}>
              Next <ChevronRightIcon />
            </Button>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Tag editor dialog (create / edit)
// ══════════════════════════════════════════════════════════════════════════════

function TagEditorDialog({ open, onOpenChange, tag, orgId, onSuccess, setToastMessage }) {
  const isEdit = !!tag;
  const createMutation = useCreateContactTag();
  const updateMutation = useUpdateContactTag();

  const [name, setName] = useState("");

  React.useEffect(() => {
    setName(tag?.name || "");
  }, [tag, open]);

  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ tagId: tag.tag_id, data: { name: trimmed } });
        setToastMessage("Tag updated");
      } else {
        await createMutation.mutateAsync({ organisation_id: orgId, name: trimmed });
        setToastMessage("Tag created");
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data;
      const msg = typeof detail === "string" ? detail
        : detail?.error || detail?.name?.[0] || "Failed to save tag";
      setToastMessage(msg);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="420px">
        <Dialog.Title>{isEdit ? "Edit tag" : "Create tag"}</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Tags let you manually group contacts (e.g. VIP, Regulars).
        </Dialog.Description>

        <Flex direction="column" gap="2" mt="4">
          <Text size="2" weight="medium">Name</Text>
          <TextField.Root
            placeholder="e.g. VIP"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim() && !saving) handleSave();
            }}
          />
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
          <Button disabled={!name.trim() || saving} onClick={handleSave}>
            {saving ? "Saving…" : isEdit ? "Update tag" : "Create tag"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Main Exported Component
// ══════════════════════════════════════════════════════════════════════════════

const TagsTab = forwardRef(function TagsTab({ orgId, setToastMessage }, ref) {
  const { contactTags, isLoading, refetch } = useContactTags(orgId);
  const deleteMutation = useDeleteContactTag();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [tagToDelete, setTagToDelete] = useState(null);

  // Drill-in state
  const [viewingTag, setViewingTag] = useState(null);

  const handleCreate = () => { setEditTag(null); setEditorOpen(true); };
  const handleEdit = (tag) => { setEditTag(tag); setEditorOpen(true); };

  // Expose actions to parent via ref
  useImperativeHandle(ref, () => ({
    create: handleCreate,
    refresh: refetch,
  }), [refetch]);

  const handleDelete = async () => {
    if (!tagToDelete) return;
    try {
      await deleteMutation.mutateAsync(tagToDelete.tag_id);
      setToastMessage("Tag deleted");
    } catch {
      setToastMessage("Failed to delete tag");
    }
    setTagToDelete(null);
  };

  // If viewing a tag's contacts, show that sub-view
  if (viewingTag) {
    return (
      <TagContactsViewer
        tag={viewingTag}
        orgId={orgId}
        onBack={() => setViewingTag(null)}
        setToastMessage={setToastMessage}
      />
    );
  }

  return (
    <>
      <Flex direction="column" gap="4" style={{ alignSelf: "stretch", flex: "1 0 0", width: "100%" }}>
        {isLoading ? (
          <Flex justify="center" align="center" style={{ flex: "1 0 0" }}>
            <Text color="gray">Loading tags…</Text>
          </Flex>
        ) : contactTags.length === 0 ? (
          <Flex direction="column" gap="2"
            style={{ justifyContent: "center", alignItems: "center", alignSelf: "stretch",
              border: "1px dashed var(--gray-a6)", borderRadius: "var(--radius-4)",
              height: "100%", flex: "1 0 0", padding: "var(--space-6)" }}>
            <Text color="gray" weight="medium">No tags yet</Text>
            <Text size="2" color="gray">Create your first tag to manually group contacts.</Text>
          </Flex>
        ) : (
          <Flex direction="column" gap="3" style={{ alignSelf: "stretch" }}>
            {contactTags.map((tag) => (
              <Card key={tag.tag_id} style={{ padding: "var(--space-4)", cursor: "pointer" }}
                onClick={() => setViewingTag(tag)}>
                <Flex justify="between" align="center" gap="3">
                  <Flex align="center" gap="3" style={{ flex: "1 0 0", minWidth: 0 }}>
                    <Badge variant="soft" size="3" color="teal">
                      <BookmarkIcon width="12" height="12" />
                      {tag.name}
                    </Badge>
                    <Badge variant="soft" color="gray" size="3">
                      <PersonIcon width="12" height="12" />
                      {tag.contact_count?.toLocaleString() ?? 0} contact{tag.contact_count !== 1 && "s"}
                    </Badge>
                    {/* <Text size="1" color="gray">Created {formatDateTime(tag.created_at)}</Text> */}
                  </Flex>
                  <Flex gap="3" onClick={(e) => e.stopPropagation()}>
                    <Tooltip content="Edit tag">
                      <IconButton variant="ghost" size="1" onClick={() => handleEdit(tag)}>
                        <Pencil1Icon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Delete tag">
                      <IconButton variant="ghost" color="red" size="1" onClick={() => setTagToDelete(tag)}>
                        <TrashIcon />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                </Flex>
              </Card>
            ))}
          </Flex>
        )}
      </Flex>

      {/* Editor dialog */}
      <TagEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        tag={editTag}
        orgId={orgId}
        onSuccess={refetch}
        setToastMessage={setToastMessage}
      />

      {/* Delete confirmation */}
      <AlertDialog.Root open={!!tagToDelete} onOpenChange={(open) => !open && setTagToDelete(null)}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Delete Tag</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Are you sure you want to delete <Text weight="bold">{tagToDelete?.name}</Text>?
            The tag will be removed from all contacts. This will not delete any contacts.
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={handleDelete}>Delete</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </>
  );
});

export default TagsTab;
